import React from "react";
import { Product } from "./types/restaurant";
import { usePaymentTheme } from "../respayment/hooks/usePaymentTheme";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import Image from "src/shared/Image";
import { useFormatAmount } from "src/business-types/laundry/modules/home/hooks/useFormatAmount";

interface ProductGridProps {
  products: Product[];
  selectedProducts: Product[];
  handleIncrement: (item: Product) => void;
  handleDecrement: (item: Product) => void;
}

const ProductGrid: React.FC<ProductGridProps> = ({ products, selectedProducts, handleIncrement, handleDecrement }) => {
  // Helper function for truncation
  const truncateText = (text: string, length: number) => (text.length > length ? `${text.slice(0, length)}...` : text);

  const { getLightBgClass, themeTextColor, themeBorderColor, themeRingColor, fontClass } = usePaymentTheme();

  // Get quantity for each product
  const getQuantity = (item: Product) => {
  const selected = selectedProducts.find(
    (order) => order._id.$oid === item._id.$oid
  );

  return selected?.quantity || 0;
};



  // const primaryColorClass = getBgClass();
  const lightBgClass = getLightBgClass();
  const textColorClass = themeTextColor;
  const borderColorClass = themeBorderColor;

  const formatAmount = useFormatAmount();

  return (
    <div className="flex-grow overflow-hidden p-2 bg-gray-50/30">
      {/* Enhanced grid with better responsive design */}
      <div className="thin-scrollbar grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 xl:grid-cols-5 gap-3 p-2 overflow-y-auto custom-scrollbar items-start w-full content-start max-h-[calc(100vh-230px)]">
        {products.slice(0, 50).map((item) => {
          const quantity = getQuantity(item);
          const hasQuantity = quantity > 0;

          return (
            <div key={item._id.$oid} onClick={() => handleIncrement(item)}
              className={`group relative flex flex-col bg-white rounded-xl shadow-md border border-gray-200 transition-all duration-300 cursor-pointer overflow-hidden hover:shadow-lg 
                hover:translate-y-[-2px] active:translate-y-0 ${hasQuantity ? `ring-2 ${themeRingColor} border-${borderColorClass.split("-")[1]}-300` : ""} min-h-[200px] h-auto self-start`}
            >
              <div className="relative w-full h-[120px] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden rounded-t-xl">
                <Image imagePath={item.image_url} className="w-full h-full object-cover" />
                {hasQuantity && (
                  <div className={`absolute top-2 right-2 bg-white ${textColorClass} rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold shadow-lg border-2 ${themeBorderColor}`}>
                    {quantity}
                  </div>
                )}
              </div>

              {/* Product Info - Enhanced Hierarchy */}
              <div className="flex flex-col flex-grow p-3">
                {/* Main Product Name */}
                <h3 className={`text-sm font-semibold text-gray-900 leading-tight mb-0.5 truncate line-clamp-2 group-hover:text-gray-700 transition-colors ${fontClass}`}>{truncateText(item.ProductName, 20)}</h3>

                {/* Arabic Name - Subtle */}
                {item.ArabicName && <h4 className="text-xs text-gray-500 font-normal truncate mb-0.5 leading-relaxed line-clamp-2">{truncateText(item.ArabicName, 35)}</h4>}

                {/* Spacer to push price to bottom */}
                <div className="flex-grow" />

                {/* Price and Action Section */}
                <div className="flex items-center justify-between mt-0.5 pt-1 border-t border-gray-100">
                  <span className={`text-base font-bold ${textColorClass}`}>{formatAmount(item.Amount)}</span>

                  {/* Quantity Controls - Always Visible but Subtle */}
                  <div className="flex items-center gap-1">
                    {hasQuantity ? (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleDecrement(item); }} className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold hover:bg-gray-200 transition-colors"> −</button>
                        <span className="text-sm font-semibold text-gray-700 min-w-[20px] text-center">{quantity}</span>
                        <button onClick={(e) => { e.stopPropagation(); handleIncrement(item); }} className="w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center text-sm font-bold hover:bg-gray-200 transition-colors"> + </button>
                      </>
                    ) : (
                      <button onClick={(e) => { e.stopPropagation(); handleIncrement(item); }}
                        className={`w-7 h-7 rounded-lg ${lightBgClass} ${textColorClass} flex items-center justify-center text-sm font-semibold hover:scale-110 transition-transform opacity-90 hover:opacity-100`}>
                        <RestaurantMenuIcon fontSize="small" className={textColorClass} />
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Active State Indicator */}
              <div
                className={`absolute inset-0 border-2 border-transparent rounded-xl group-hover:border-${borderColorClass.split("-")[1]}-200 transition-all duration-300 pointer-events-none ${hasQuantity ? `border-${borderColorClass.split("-")[1]}-300` : ""
                  }`}
              />
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {products.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">

          <p className="text-lg font-medium mb-2">No products found</p>
          <p className="text-sm text-gray-400">Try selecting a different category</p>
        </div>
      )}
    </div>
  );
};

export default ProductGrid;
